import type { CompanyNeedAddress } from '@/modules/companyNeed/types/address';
import type { ContractType } from '@/modules/companyNeed/types/contract';
import type { CreatorType } from '@/modules/companyNeed/types/creator';
import type { HistoryEntry } from '@/modules/companyNeed/types/history';
import type { PeriodPreference } from '@/modules/companyNeed/types/periodPreference';
import type { CompanyNeedPriority } from '@/modules/companyNeed/types/priority';
import type { NeedRecruitmentCardAlerts } from '@/modules/companyNeed/types/recruitment';
import type { MissionStatus } from '@/modules/companyNeed/views/details/missions/types';
import type { Timestamp } from '@/types/common';
import type { IHabilitationDocument } from '@/types/documents';
import type { JobExperience } from '@/types/job';
import type {
	JobOfferPublicationStatus,
	JobOfferStatus,
} from '@/types/jobOffer';
import type { WorkerMissionStatus } from '@/types/mission';
import type { Remuneration } from '@/types/remuneration';
import type { ActiveProposal } from '../activeProposal/types';

export enum CompanyNeedLoadingState {
	COMPANY_NEED = 'COMPANY_NEED',
	MATCHING_WORKERS = 'MATCHING_WORKERS',
	CANDIDATES = 'CANDIDATES',
	POOL_WORKERS = 'POOL_WORKERS',
	REJECTED_PROFILES = 'REJECTED_PROFILES',
	MISSIONS = 'MISSIONS',
	RECRUITMENT = 'RECRUITMENT',
	COMPANY_CONTACTS = 'COMPANY_CONTACTS',
}

export type CompanyNeedReloadSection =
	| 'companyNeed'
	| 'matchingWorkers'
	| 'candidates'
	| 'poolWorkers'
	| 'rejectedProfiles'
	| 'missions'
	| 'recruitment'
	| 'companyContacts';

export enum CompanyNeedStatus {
	CREATED = 'created',
	MATCHING_DONE = 'matchingDone',
	OPEN = 'open',
	ARCHIVED = 'archived',
}

export interface JobOfferDetails {
	id: string;
	status: JobOfferStatus;
	publishedAt: Timestamp | null;
	hasRepublish?: boolean;
	canBeDeleted?: boolean;
}
export interface CompanyNeed {
	_id: string;
	name: string;
	priority: CompanyNeedPriority | null;
	companyId: string;
	companyName: string;
	jobId: string;
	address: CompanyNeedAddress;
	missions: number;
	poolProfileNumber: number;
	refusedProfileNumber: number;
	ATSProfileNumber: number;
	// Optional fields
	// agencyId?: string;
	// agencyName?: string;
	contractType?: ContractType | null;
	positionsNumber: number;
	remuneration?: Remuneration;
	experienceId?: JobExperience | null;
	habilitations?: IHabilitationDocument[] | null;
	startDate?: Date | null;
	endDate?: Date | null;
	createdAt?: string | null;
	description?: string | null;
	internalNotes?: string | null;
	jobOffer?: JobOfferDetails;
	periodPreferences: PeriodPreference[];
	weekEndRequired: boolean;
	hasEmbedding: boolean;
	canDelete: boolean;
}

export enum CompanyNeedStaffingStatus {
	PRESELECTED = 'PRESELECTED',
	CONTACTED = 'CONTACTED',
	INTERVIEW = 'INTERVIEW',
	INTERVIEW_COMPANY = 'INTERVIEW_COMPANY',
	COMPANY_PROPOSED = 'COMPANY_PROPOSED',
	MISSION_PROPOSED = 'MISSION_PROPOSED',
	REJECTED = 'REJECTED',
}

export interface CompanyNeedStaffingState {
	[CompanyNeedStaffingStatus.PRESELECTED]: number;
	[CompanyNeedStaffingStatus.CONTACTED]: number;
	[CompanyNeedStaffingStatus.INTERVIEW]: number;
	[CompanyNeedStaffingStatus.INTERVIEW_COMPANY]: number;
	[CompanyNeedStaffingStatus.COMPANY_PROPOSED]: number;
	[CompanyNeedStaffingStatus.MISSION_PROPOSED]: number;
	[CompanyNeedStaffingStatus.REJECTED]: number;
}

export interface JobOfferListing {
	status: JobOfferStatus;
	candidateToTreat: number;
	publishedAt: number;
}

export interface CompanyNeedPopulated {
	id: string;
	name: string;
	companyId: string;
	companyName: string;
	staffing: Partial<CompanyNeedStaffingState>;
	poolProfileNumber: number;
	missions: number;
	priority?: CompanyNeedPriority | null;
	jobId?: string | null;
	jobName?: string | null;
	jobOffer?: JobOfferListing | null;
	updatedAt?: string;
	canDelete: boolean;
}

export interface CompanyNeedQuickActionResponse {
	id: string;
	name: string;
	status: CompanyNeedStatus;
	companyId: string;
	companyName: string;
	jobId: string | null;
	jobName: string | null;
	poolProfilesLength: number;
	numberMissionsLength: number;
	createdAt: string;
	updatedAt: string;
	fromJobOffer: Record<string, boolean>;
}

export enum UnavailabilityType {
	MISSION = 'mission',
	DEFAULT = 'default',
}

export enum RangeType {
	FROM = 'from',
	TO = 'to',
}

export interface Unavailability {
	unvailabilityType: UnavailabilityType;
	rangeType: RangeType;
	date: Timestamp;
}

export interface CreateCompanyNeed {
	name: string;
	jobId: string;
	companyId: string;
	companyName: string;
	agencyName: string;
	address: CompanyNeedAddress;
	creatorType?: CreatorType;
	priority?: CompanyNeedPriority;
	startDate?: Date;
	endDate?: Date;
	contractType?: ContractType;
	positions?: number;
	description?: string;
	internalNotes?: string;
	habilitations?: string[];
	experience?: JobExperience;
	payFrequencyId?: string;
	salaryMin?: string;
	salaryMax?: string;
	periodPreferences?: PeriodPreference[];
	weekEndRequired?: boolean;
	poolWorkerIds?: string[];
}

export interface UpdateCompanyNeed {
	name?: string;
	priority?: CompanyNeedPriority;
	startDate?: Date | null;
	endDate?: Date | null;
	contractType?: ContractType;
	positions?: number;
	description?: string;
	internalNotes?: string;
	habilitations?: string[];
	experience?: JobExperience | null;
	address?: CompanyNeedAddress;
	payFrequencyId?: string;
	salaryMin?: string;
	salaryMax?: string;
	periodPreferences?: PeriodPreference[];
	weekEndRequired?: boolean;
}

export interface CompanyNeedResponse {
	id: string;
	status: CompanyNeedStatus;
}

export interface CreateCompanyNeedResponse extends CompanyNeedResponse {}

export interface UpdateCompanyNeedResponse extends CompanyNeedResponse {}

export interface ArchiveCompanyNeedResponse extends CompanyNeedResponse {}

export interface ReopenCompanyNeedResponse extends CompanyNeedResponse {}

export interface DuplicateCompanyNeedResponse extends CompanyNeedResponse {}

export enum CompanyNeedListSort {
	NAME = 'name',
	COMPANY_NAME = 'companyName',
	CREATED_AT = 'createdAt',
	UPDATED_AT = 'updatedAt',
	MISSIONS_COUNT = 'missionsCount',
	POOL_COUNT = 'poolCount',
	JOB_NAME = 'jobName',
	PRIORITY = 'priority',
	ATS_COUNT = 'atsCount',
	PRESELECTED_COUNT = 'preselectedCount',
	CONTACTED_COUNT = 'contactedCount',
	INTERVIEW_COUNT = 'interviewCount',
	COMPANY_PROPOSED_COUNT = 'companyProposedCount',
	INTERVIEW_COMPANY_COUNT = 'interviewCompanyCount',
	MISSION_PROPOSED_COUNT = 'missionProposedCount',
}

export enum CompanyNeedListOrder {
	ASC = 'asc',
	DESC = 'desc',
}

export interface CompanyNeedListQueryParams {
	page?: number;
	limit?: number;
	search?: string;
	agencyId?: string;
	hasJobOffer?: boolean;
	jobOfferStatus?: JobOfferStatus;
	sort?: CompanyNeedListSort;
	order?: CompanyNeedListOrder;
	companyIds?: string;
	jobOfferPublicationStatus?: JobOfferPublicationStatus;
	isArchived?: boolean;
	status?: CompanyNeedStatus;
	priority?: CompanyNeedPriority;
	hasMissions?: boolean;
	jobIds?: string;
	excludedJobIds?: string[];
	latitude?: number;
	longitude?: number;
	maxDistanceKm?: number;
	from?: string;
	to?: string;
}

export interface CompanyNeedMetadata {
	total: number;
	byJob: { jobId: string; count: number }[];
	byCompany: { companyId: string; companyName: string; count: number }[];
}

export interface CompanyNeedPaginatedResponse<T> {
	items: T[];
	offset: number;
	hasNextPage: boolean;
	hasPreviousPage: boolean;
	limit: number;
	page: number;
	total: number;
	totalPages: number;
}

export interface CompanyNeedDetailsResponse {
	id: string;
	name: string;
	priority: CompanyNeedPriority;
	companyId: string;
	companyName: string;
	missionsCount: number;
	poolProfileNumber: number;
	refusedProfileNumber: number;
	atsProfileNumber: number;
	jobId: string | null;
	jobName: string | null;
	address: CompanyNeedAddress | null;
	positionsNumber: number | null;
	payFrequencyId: string | null;
	salaryFrom: number | null;
	salaryTo: number | null;
	experience: JobExperience | null;
	contractType: ContractType | null;
	description: string | null;
	internalNotes: string | null;
	habilitations: string[] | null;
	jobOffer: JobOfferDetails | null;
	startDate: Date | null;
	endDate: Date | null;
	createdAt: string | null;
	periodPreferences: PeriodPreference[];
	weekEndRequired: boolean;
	hasEmbedding: boolean;
	canDelete: boolean;
	status: CompanyNeedStatus;
	history: {
		id: string;
		actionType: CompanyNeedStatus;
		userId: string;
		userFirstName: string;
		userLastName: string;
		createdAt: number;
	}[];
}

export interface WorkersQueryParams {
	search?: string;
	includeProposedOnClosedMissions?: boolean;
}

export enum WorkerContactType {
	VOICE_MESSAGE = 'VOICE_MESSAGE',
	PHONE = 'PHONE',
	SMS = 'SMS',
	EMAIL = 'EMAIL',
	AGENCY_VISIT = 'AGENCY_VISIT',
}

export interface WorkerContact {
	type: WorkerContactType;
	date: string;
}

export interface WorkerInterview {
	id?: string;
	date: string;
	start: string;
	end?: string;
	comment?: string;
	contactId: string;
	contactName?: string;
	interviewType?: 'AGENCY' | 'CLIENT';
}

export interface WorkerMission {
	missionId: string;
	missionName: string;
	missionStatus: MissionStatus;
	workerStatus: WorkerMissionStatus;
	availableStatuses?: WorkerMissionStatus[];
}

export interface RecruitmentWorker {
	workerId: string;
	firstname: string;
	lastname: string;
	email: string | null;
	phone: string;
	// biome-ignore lint/suspicious/noExplicitAny: Init Biome
	picture: any;
	postulatedToJobOffer: boolean;
	following: HistoryEntry[];
	contact: WorkerContact | null;
	interview: WorkerInterview | null;
	interviews?: WorkerInterview[];
	missions?: WorkerMission[];
	proposals?: ActiveProposal[];
	alerts?: NeedRecruitmentCardAlerts[];
}

export interface CompanyNeedWorkersResponse {
	prequalified: RecruitmentWorker[];
	contacted: RecruitmentWorker[];
	interview: RecruitmentWorker[];
	proposedToCompany: RecruitmentWorker[];
	interviewCompany: RecruitmentWorker[];
	missionProposed: RecruitmentWorker[];
}

export interface CompanyNeedByCompany {
	id: string;
	companyId: string;
	companyName: string;
	name: string;
	poolProfileNumber: number;
	staffing: Partial<CompanyNeedStaffingState>;
	missions: number;
	priority: CompanyNeedPriority;
	updatedAt: string;
	agencyId?: string;
	agencyName?: string;
	jobId?: string;
	jobOffer?: JobOfferListing;
}
